import { useState } from "react";

import { Link } from "react-router-dom";

import {
  useAtualizarEmpresa,
  useCriarEmpresa,
  useEmpresas,
} from "../../lib/cadastros/hooks";
import type { EmpresaOut } from "../../lib/cadastros/tipos";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";
import {
  FormularioEmpresa,
  type DadosEmpresaForm,
} from "../cadastros/FormularioEmpresa";

/*
 * Página de Empresas do módulo de Cadastros (FE-4 CP-B).
 *
 * Lista (tabela) das empresas no escopo do usuário (`useEmpresas`) com estados
 * loading/erro/vazio claros. Acima da tabela, o botão "Nova empresa" (gated por
 * `cadastrar_infraestrutura`) abre o `FormularioEmpresa` em modo criação; cada
 * linha tem "Editar" (mesmo formulário em modo edição) e "Contas" (navega para a
 * rota nested daquela empresa).
 *
 * Estado de TELA (client-state efêmero, `useState` — não Zustand): qual formulário
 * está aberto. As mutações (`useCriarEmpresa`/`useAtualizarEmpresa`) já invalidam
 * a lista no `onSuccess` do hook — em sucesso, fechamos o formulário e a tabela
 * revalida sozinha.
 *
 * Empresas NÃO têm delete: desativar = PATCH `ativa=false` (via toggle do form).
 *
 * SÓ classes Tailwind dos tokens CPMH.
 */

const ACAO_CADASTRAR_INFRAESTRUTURA = "cadastrar_infraestrutura";

/** Qual formulário está aberto na tela. `null` = nenhum. */
type FormAberto =
  | { tipo: "criar" }
  | { tipo: "editar"; empresa: EmpresaOut }
  | null;

export function PaginaEmpresas(): JSX.Element {
  const consulta = useEmpresas();
  const criar = useCriarEmpresa();
  const atualizar = useAtualizarEmpresa();
  const podeCadastrar = useTemPermissao(ACAO_CADASTRAR_INFRAESTRUTURA);

  const [form, setForm] = useState<FormAberto>(null);

  function salvarCriacao(dados: DadosEmpresaForm): void {
    criar.mutate(
      {
        cnpj: dados.cnpj,
        razao_social: dados.razao_social,
        // Fantasia vazia vira `null` (campo opcional/anulável no wire).
        fantasia: dados.fantasia === "" ? null : dados.fantasia,
      },
      {
        onSuccess: () => setForm(null),
      },
    );
  }

  function salvarEdicao(empresa: EmpresaOut, dados: DadosEmpresaForm): void {
    atualizar.mutate(
      {
        id: empresa.id,
        // `cnpj` NÃO entra no PATCH (não é editável no backend).
        corpo: {
          razao_social: dados.razao_social,
          fantasia: dados.fantasia === "" ? null : dados.fantasia,
          ativa: dados.ativa,
        },
      },
      {
        onSuccess: () => setForm(null),
      },
    );
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando empresas…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as empresas.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const empresas = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      {podeCadastrar && (
        <div>
          <button
            type="button"
            onClick={() => setForm({ tipo: "criar" })}
            className="rounded-md border border-flame bg-flame px-4 py-1 font-subtitulo text-snow"
          >
            Nova empresa
          </button>
        </div>
      )}

      {empresas.length === 0 ? (
        <p className="font-corpo text-base text-off-black">
          Nenhuma empresa cadastrada.
        </p>
      ) : (
        <table className="w-full border-collapse font-corpo text-base text-off-black">
          <thead>
            <tr className="border-b border-platinum text-left">
              <th className="py-2 pr-4 font-subtitulo">Razão social</th>
              <th className="py-2 pr-4 font-subtitulo">Fantasia</th>
              <th className="py-2 pr-4 font-subtitulo">CNPJ</th>
              <th className="py-2 pr-4 font-subtitulo">Ativa</th>
              <th className="py-2 pr-4 font-subtitulo">Ações</th>
            </tr>
          </thead>
          <tbody>
            {empresas.map((empresa) => (
              <tr key={empresa.id} className="border-b border-platinum">
                <td className="py-2 pr-4">{empresa.razao_social}</td>
                <td className="py-2 pr-4">{empresa.fantasia ?? "—"}</td>
                <td className="py-2 pr-4">{empresa.cnpj}</td>
                <td className="py-2 pr-4">{empresa.ativa ? "Sim" : "Não"}</td>
                <td className="py-2 pr-4">
                  <div className="flex gap-3">
                    {podeCadastrar && (
                      <button
                        type="button"
                        onClick={() =>
                          setForm({ tipo: "editar", empresa })
                        }
                        className="text-flame hover:underline"
                      >
                        Editar
                      </button>
                    )}
                    <Link
                      to={`${empresa.id}/contas`}
                      className="text-flame hover:underline"
                    >
                      Contas
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form?.tipo === "criar" && (
        <FormularioEmpresa
          aoSalvar={salvarCriacao}
          aoCancelar={() => setForm(null)}
          salvando={criar.isPending}
          erro={criar.error}
        />
      )}

      {form?.tipo === "editar" && (
        <FormularioEmpresa
          empresa={form.empresa}
          aoSalvar={(dados) => salvarEdicao(form.empresa, dados)}
          aoCancelar={() => setForm(null)}
          salvando={atualizar.isPending}
          erro={atualizar.error}
        />
      )}
    </div>
  );
}
